import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Cloud, Lock, Eye, EyeOff, CheckCircle, XCircle, 
  Shield, AlertTriangle, Loader2, Database, Server, Trash2, ExternalLink, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple encryption for localStorage
const encryptCredentials = (creds) => {
  return btoa(JSON.stringify(creds));
};

const decryptCredentials = (encrypted) => {
  try {
    return JSON.parse(atob(encrypted));
  } catch {
    return null;
  }
};

const GCPConnect = ({ isOpen, onClose, onConnected }) => {
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [billingAccountId, setBillingAccountId] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [rememberCredentials, setRememberCredentials] = useState(false);

  // Load saved credentials on mount
  useEffect(() => {
    const saved = localStorage.getItem('gcp_creds');
    if (saved) {
      const decrypted = decryptCredentials(saved);
      if (decrypted) {
        setServiceAccountJson(JSON.stringify(decrypted.service_account, null, 2));
        setBillingAccountId(decrypted.billing_account_id || '');
        setRememberCredentials(true);
      }
    }
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target.result);
          setServiceAccountJson(JSON.stringify(json, null, 2));
          toast.success('Service account file loaded');
        } catch (err) {
          toast.error('Invalid JSON file');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleQuickTest = async () => {
    if (!serviceAccountJson) {
      toast.error('Please provide service account JSON first');
      return;
    }

    let parsedJson;
    try {
      parsedJson = JSON.parse(serviceAccountJson);
    } catch (err) {
      toast.error('Invalid JSON format');
      return;
    }

    if (parsedJson.type !== 'service_account') {
      toast.error('JSON must be a service account key file');
      return;
    }

    setIsTesting(true);
    setTestResults(null);

    try {
      const credentials = {
        service_account: parsedJson,
        billing_account_id: billingAccountId || null
      };

      const response = await axios.post(`${API_URL}/api/gcp/quick-test`, credentials);
      setTestResults(response.data);
      
      if (response.data.success) {
        toast.success('All permissions verified!');
      } else {
        toast.warning(response.data.fix_suggestion || 'Some permissions missing');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Test failed';
      setTestResults({
        success: false,
        error: message,
        fix_suggestion: 'Check your service account key'
      });
      toast.error(message);
    } finally {
      setIsTesting(false);
    }
  };

  const handleConnect = async () => {
    if (!serviceAccountJson) {
      toast.error('Please provide service account JSON');
      return;
    }

    let parsedJson;
    try {
      parsedJson = JSON.parse(serviceAccountJson);
    } catch (err) {
      toast.error('Invalid JSON format');
      return;
    }

    if (parsedJson.type !== 'service_account') {
      toast.error('JSON must be a service account key file');
      return;
    }

    setIsConnecting(true);
    setConnectionStatus(null);

    try {
      const credentials = {
        service_account: parsedJson,
        billing_account_id: billingAccountId || null
      };

      const response = await axios.post(`${API_URL}/api/gcp/connect`, credentials);
      
      if (response.data.success) {
        setConnectionStatus({
          success: true,
          ...response.data.account_info
        });

        if (rememberCredentials) {
          localStorage.setItem('gcp_creds', encryptCredentials(credentials));
        } else {
          localStorage.removeItem('gcp_creds');
        }

        sessionStorage.setItem('gcp_session', encryptCredentials(credentials));

        toast.success('Connected to GCP successfully!');
        
        if (onConnected) {
          onConnected(credentials, response.data.account_info);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Connection failed';
      setConnectionStatus({
        success: false,
        error: message
      });
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleClearSaved = () => {
    localStorage.removeItem('gcp_creds');
    sessionStorage.removeItem('gcp_session');
    setServiceAccountJson('');
    setBillingAccountId('');
    setRememberCredentials(false);
    setConnectionStatus(null);
    toast.success('Saved credentials cleared');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gcp-connect-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-red-500" />
            Connect to Google Cloud
          </DialogTitle>
          <DialogDescription>
            Upload your GCP service account key file to view billing data.
          </DialogDescription>
        </DialogHeader>

        {/* Security Notice */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Shield className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Your data stays secure
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                Service account keys are stored only in your browser. We query GCP directly - 
                <strong> no data is ever stored on our servers.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 py-2">
          {/* File Upload */}
          <div className="space-y-2">
            <Label>Upload Service Account Key File</Label>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => document.getElementById('gcp-file-input').click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Choose JSON File
              </Button>
              <input
                id="gcp-file-input"
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* JSON Editor */}
          <div className="space-y-2">
            <Label htmlFor="service_account_json">Service Account JSON *</Label>
            <Textarea
              id="service_account_json"
              placeholder='{"type": "service_account", "project_id": "...", ...}'
              value={serviceAccountJson}
              onChange={(e) => setServiceAccountJson(e.target.value)}
              className="font-mono text-xs h-40"
              data-testid="gcp-json-input"
            />
            <p className="text-xs text-slate-400">
              Download from GCP Console → IAM & Admin → Service Accounts → Keys
            </p>
          </div>

          {/* Billing Account ID */}
          <div className="space-y-2">
            <Label htmlFor="billing_account_id">Billing Account ID (optional)</Label>
            <Input
              id="billing_account_id"
              placeholder="billingAccounts/XXXXXX-XXXXXX-XXXXXX"
              value={billingAccountId}
              onChange={(e) => setBillingAccountId(e.target.value)}
            />
            <p className="text-xs text-slate-400">
              Found in GCP Console → Billing → Account Management
            </p>
          </div>

          {/* Required Permissions Hint */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <strong>Required Role:</strong> Billing Account Viewer
            </p>
            <p className="text-xs text-amber-600 mt-1">
              For detailed costs, enable BigQuery Billing Export in GCP Console.
            </p>
            <a 
              href="https://cloud.google.com/billing/docs/how-to/export-data-bigquery" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-amber-600 hover:underline flex items-center gap-1 mt-1"
            >
              View GCP documentation <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Quick Test Button */}
          <div className="border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Quick Permission Test</p>
                <p className="text-xs text-slate-400">Validate service account and billing access</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleQuickTest}
                disabled={isTesting || !serviceAccountJson}
                data-testid="gcp-quick-test-btn"
              >
                {isTesting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-1" />
                    Quick Test
                  </>
                )}
              </Button>
            </div>
            
            {/* Test Results */}
            {testResults && (
              <div className={`mt-3 p-2 rounded text-sm ${
                testResults.success 
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              }`}>
                {testResults.success ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      All permissions verified!
                    </div>
                    {testResults.tests && (
                      <div className="text-xs space-y-0.5 ml-5">
                        <p>✓ Service Account Authentication</p>
                        <p>✓ Billing API Access</p>
                        {testResults.tests.project_billing_access && <p>✓ Project Billing Access</p>}
                        {testResults.account_info && (
                          <>
                            <p className="text-slate-400 dark:text-slate-400">
                              Project: {testResults.account_info.project_id}
                            </p>
                            <p className="text-slate-400 dark:text-slate-400">
                              Billing Accounts: {testResults.account_info.billing_accounts_found}
                            </p>
                          </>
                        )}
                      </div>
                    )}
                    {testResults.note && (
                      <p className="text-xs text-amber-600 mt-2 ml-5">
                        Note: {testResults.note}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 font-medium">
                      <XCircle className="w-4 h-4" />
                      {testResults.error || 'Test failed'}
                    </div>
                    {testResults.fix_suggestion && (
                      <p className="text-xs ml-5">
                        Fix: {testResults.fix_suggestion}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Remember Credentials */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberCredentials}
                onChange={(e) => setRememberCredentials(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-400 dark:text-slate-400">
                Remember credentials (browser only)
              </span>
            </label>
            {localStorage.getItem('gcp_creds') && (
              <Button variant="ghost" size="sm" onClick={handleClearSaved} className="text-red-500">
                <Trash2 className="w-4 h-4 mr-1" />
                Clear saved
              </Button>
            )}
          </div>

          {/* Connection Status */}
          {connectionStatus && (
            <div className={`p-3 rounded-lg border ${
              connectionStatus.success 
                ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'
                : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {connectionStatus.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <div>
                  <p className={`text-sm font-medium ${
                    connectionStatus.success ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    {connectionStatus.success ? 'Connected Successfully' : 'Connection Failed'}
                  </p>
                  {connectionStatus.success ? (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 space-y-0.5">
                      <p>Project: {connectionStatus.project_id}</p>
                      <p>Service Account: {connectionStatus.service_account}</p>
                      <p>Billing Accounts: {connectionStatus.billing_accounts_found}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-red-600">{connectionStatus.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConnect} 
            disabled={isConnecting}
            className="bg-red-500 hover:bg-red-600"
            data-testid="gcp-connect-btn"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Cloud className="w-4 h-4 mr-2" />
                Connect
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Hook to get current GCP credentials from session
export const useGCPCredentials = () => {
  const [credentials, setCredentials] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const sessionCreds = sessionStorage.getItem('gcp_session');
    if (sessionCreds) {
      const decrypted = decryptCredentials(sessionCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
        return;
      }
    }
    
    const savedCreds = localStorage.getItem('gcp_creds');
    if (savedCreds) {
      const decrypted = decryptCredentials(savedCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
      }
    }
  }, []);

  const disconnect = () => {
    sessionStorage.removeItem('gcp_session');
    setCredentials(null);
    setIsConnected(false);
  };

  const updateCredentials = (newCreds) => {
    setCredentials(newCreds);
    setIsConnected(true);
    sessionStorage.setItem('gcp_session', encryptCredentials(newCreds));
  };

  return { credentials, isConnected, disconnect, updateCredentials };
};

export default GCPConnect;
